import classNames from 'clsx';
import { isEmpty } from 'es-toolkit/compat';
import PropTypes from 'prop-types';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { player } from 'src/client/player';
import InkButton from 'src/common/components/shared/button/InkButton';
import NakedButton from 'src/common/components/shared/button/NakedButton';
import Popover from 'src/common/components/shared/Popover';
import { boostTooltipDisplayCount } from 'src/common/constants/cookies';
import {
  BOOST_INTRO_TOOLTIP,
  BOOST_TOOLTIP_OPT_IN_BTN,
  BOOST_TOOLTIP_OPT_OUT_BTN,
} from 'src/common/constants/localizations/boost-aka-switch';
import { playerStatuses } from 'src/common/constants/playerStatuses';
import { useOnClickOutside } from 'src/common/hooks/useOnClickOutside';
import usePrevious from 'src/common/hooks/usePrevious';
import { useWindowSize } from 'src/common/hooks/useWindowSize';
import { useStores } from 'src/common/providers';
import { LocationAndLocalizationContext } from 'src/common/providers/LocationAndLocalizationProvider';
import { selectBoostTooltipEnabled } from 'src/common/selectors/config';
import { selectIsUserSubscribed } from 'src/common/selectors/me';
import assetUrl from 'src/common/utils/assetUrl';
import { getBoostNextPlayButtonWidth } from 'src/common/utils/boost';
import { isMedium } from 'src/common/utils/breakpoints';
import { getCookie, setCookie } from 'src/common/utils/cookie';
import css from './boost-intro-popover.module.scss';

function BoostIntroPopover({ breakpoint, isNowPlaying, nextPlayButtonRef }) {
  const [playingHasOccurred, setPlayingHasOccurred] = useState(false);
  const [popoverOffset, setPopoverOffset] = useState({});
  const isPopoverEnabled = useSelector(selectBoostTooltipEnabled);
  const [showBoostPopover, setShowBoostPopover] = useState(false);
  const { playerStore } = useStores();
  const playerStatus = playerStore.playerStatus();
  const metadata = playerStore.metadata();
  const boostMetadata = metadata?.boost || {};
  const isBoostStation = metadata?.isBoostLoaded;
  const isUserSubscribed = useSelector(selectIsUserSubscribed);
  const tunedGuideId = playerStore.currentGuideItem()?.playableGuideId;
  const previousTunedGuideId = usePrevious(tunedGuideId);
  const isLiveSeek = isUserSubscribed && metadata?.isLiveSeek;
  const isVideoAdModalOpen = playerStore.isVideoAdLoaded();
  const ref = useRef();
  const [width] = useWindowSize();
  const isMediumBreakpoint = isMedium(breakpoint);
  const boostNextPlayButtonWidth = getBoostNextPlayButtonWidth(isMediumBreakpoint);
  const tooltipDisplayCount = getCookie(boostTooltipDisplayCount.name);

  const handleClose = (isOptInAction = false) => {
    setShowBoostPopover(false);
    player.appReporter.reportBoostSwitch('tooltip', isOptInAction);
    setCookie(boostTooltipDisplayCount.name, null, 1);
  };

  const generateTooltipOffset = useCallback(() => {
    const { left: nextButtonLeftOffset } =
      nextPlayButtonRef?.current?.getBoundingClientRect() || {};
    const arrowLeftOffset = 203; // popover arrow left offset
    const arrowWidth = 12; // $arrowBorderSize in css
    const leftOffset =
      nextButtonLeftOffset -
      (boostNextPlayButtonWidth / 2 - (boostNextPlayButtonWidth - (arrowLeftOffset + arrowWidth)));

    return {
      left: `${leftOffset}px`,
      right: 0,
    };
  }, [boostNextPlayButtonWidth, nextPlayButtonRef]);

  useOnClickOutside(ref, () => {
    if (showBoostPopover && !isVideoAdModalOpen) {
      handleClose();
    }
  });

  useEffect(() => {
    if (!width || !showBoostPopover) {
      return;
    }

    const { left, right } = generateTooltipOffset() || {};

    if (isMediumBreakpoint) {
      return setPopoverOffset({ left });
    }

    return setPopoverOffset({ right });
  }, [generateTooltipOffset, width, isMediumBreakpoint, showBoostPopover]);

  // NOTE: The purpose of playingHasOccurred is we do not want to show the ToolTip while Preroll is connecting,
  // The reason for this is once the app attempts preroll, we cannot stop it until after it has started...
  // and if the user attempts to 'switch' during the time preroll is loading, the user will still hear preroll before they hear their switch station
  // But, the ask from the business is to NOT run preroll on a switch station....
  // So, since preroll runs through the IMA player, which is seperate from Web Tuner,
  // we need to know that playerStatus has reached a playback state (either mediaAd or stream has started playback),
  // before we can safely show the Tooltip
  // at which point it is safe to stop our ads player or our Tuner, and switch the stream
  useEffect(() => {
    if (!playingHasOccurred) {
      setPlayingHasOccurred(playerStatus === playerStatuses.playing);
    }
  }, [playerStatus, playingHasOccurred]);

  useEffect(() => {
    if (previousTunedGuideId !== tunedGuideId) {
      // NOTE: Reset this state whenever the station changes & return so to wait for playerStatus to be 'playing' again
      return setPlayingHasOccurred(false);
    }
  }, [previousTunedGuideId, tunedGuideId]);

  useEffect(() => {
    // NOTE: Show popover if the following conditions are met:
    // 1. Popover is enabled via RM config
    // 2. Popover is currently closed
    // 3. If tooltip has not been shown - Future TODO: Popover Display cap has not been breached
    // 4. boostMetadata (via NP) is hydrated (confirms there is metaData that we can use to display the switch statio in the UI)
    // 5. boost has not been initialized (isBoostStation is not set until after Tune.ashx response is processed,
    //    but isBoostFeaturedInPlayer is set immediately after the user's boost click occurs, which is when we should close the tooltip)
    // 6. The current station is not a Boost station (isBoostStation)
    // 7. We do not want to begin switch while in an initial loading state,
    //    so we wait for some type of playback (preroll or stream) to occur

    const shouldShowBoostPopover =
      isPopoverEnabled &&
      !showBoostPopover &&
      !tooltipDisplayCount &&
      !isEmpty(boostMetadata) &&
      !isBoostStation &&
      playingHasOccurred &&
      !isLiveSeek;

    if (shouldShowBoostPopover) {
      player.appReporter.reportBoostTooltip();
      setShowBoostPopover(true);
    }
  }, [
    boostMetadata,
    isBoostStation,
    isPopoverEnabled,
    playingHasOccurred,
    showBoostPopover,
    tooltipDisplayCount,
    isLiveSeek,
  ]);

  const { getLocalizedText } = useContext(LocationAndLocalizationContext);

  const handleTrySwitch = () => {
    handleClose(true);
    player.playNext();
  };

  return (
    <Popover
      id="boostIntroPopover"
      isOpen={showBoostPopover}
      className={classNames(
        css.popoverContainer,
        isNowPlaying ? css.nowPlayingPositioning : css.playerPositioning,
      )}
      contentClassName={css.popoverContent}
      styleOverride={popoverOffset}
      handleCloseClick={() => handleClose()}
      popoverRef={ref}
      showCornerClose
    >
      <img className={css.switchLogo} src={assetUrl('assets/img/shared/switch-logo.svg')} />
      <div className={css.tooltipText}>{getLocalizedText(BOOST_INTRO_TOOLTIP)}</div>
      <InkButton
        id="boostOptIn"
        className={css.dialogCtaButton}
        label={getLocalizedText(BOOST_TOOLTIP_OPT_IN_BTN)}
        onClick={handleTrySwitch}
      />
      <NakedButton
        id="boostOptOut"
        className={css.dialogCtaButton}
        label={getLocalizedText(BOOST_TOOLTIP_OPT_OUT_BTN)}
        onClick={() => handleClose()}
      />
    </Popover>
  );
}

BoostIntroPopover.propTypes = {
  nextPlayButtonRef: PropTypes.object.isRequired,
  breakpoint: PropTypes.number.isRequired,
  isNowPlaying: PropTypes.bool,
};

export default BoostIntroPopover;
